import React, { useRef, useEffect, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod/v4";
import { call as rpc } from "../../../common/jsonRpcRoutes";
import type { LoggedInUser } from "../hooks/useAccountDetection";
import AlertBanner from "./AlertBanner";

const signInDetailSchema = z.object({
  accessToken: z.string().min(1),
  user: z.object({ email: z.string().optional() }).optional(),
});
const signInErrorDetailSchema = z.object({ message: z.string().optional() });

interface DmnSignInProps {
  /** Auth API host URL (e.g. https://api-auth-dev.designmynight.com) */
  readonly authApiHost: string;
  /** OAuth client ID */
  readonly clientId: string;
  /** OAuth client secret */
  readonly clientSecret: string;
  /** Callback when social login succeeds with user profile data */
  readonly onLoginSuccess: (user: LoggedInUser) => void;
  /** Optional callback when social login fails */
  readonly onError?: (error: string) => void;
  /**
   * Called instead of `onError` when the social account has no DMN profile yet,
   * so the guest can carry on with their social email pre-filled.
   */
  readonly onProfileIncomplete?: (email: string) => void;
  /** When present, links the authenticated session to this pending booking server-side. */
}

/**
 * Wraps the external `<dmn-sign-in>` web component from auth-components.
 * Listens for `signin` and `signinerror` CustomEvents, exchanges the
 * OAuth token for a user profile via `/loginWithOAuthToken`, and calls
 * back with a `LoggedInUser` object.
 */
export default function DmnSignIn({
  authApiHost,
  clientId,
  clientSecret,
  onLoginSuccess,
  onError,
  onProfileIncomplete,
}: DmnSignInProps): React.ReactElement {
  const ref = useRef<HTMLElement>(null);
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSignIn = useCallback(
    async (event: Event) => {
      const raw = event instanceof CustomEvent ? event.detail : undefined;
      const parsed = signInDetailSchema.safeParse(raw);
      if (!parsed.success) return;
      setLoading(true);
      setError(null);
      try {
        const result = await rpc("/loginWithOAuthToken", {
          access_token: parsed.data.accessToken,
        });
        if (result.success && result.user) {
          onLoginSuccess({
            first_name: result.user.first_name,
            last_name: result.user.last_name,
            email: result.user.email,
            phone: result.user.phone ?? undefined,
            dob: result.user.dob ?? undefined,
            points_balance: result.user.points_balance ?? undefined,
            custom_details: result.user.custom_details ?? undefined,
          });
          return;
        }
        const socialEmail =
          result.error_code === "profile_incomplete" ? (result.email ?? parsed.data.user?.email) : undefined;
        if (socialEmail) {
          onProfileIncomplete?.(socialEmail);
        } else {
          const msg = result.error ?? t("social_login_error", "Login failed. Please try again.");
          setError(msg);
          onError?.(msg);
        }
      } catch (_err: unknown) {
        const msg = t("social_login_failed", "Login failed. Please try again.");
        setError(msg);
        onError?.(msg);
      } finally {
        setLoading(false);
      }
    },
    [onLoginSuccess, onError, onProfileIncomplete, t],
  );

  const handleSignInError = useCallback(
    (event: Event) => {
      const raw = event instanceof CustomEvent ? event.detail : undefined;
      const parsed = signInErrorDetailSchema.safeParse(raw);
      const msg =
        parsed.success && parsed.data.message
          ? parsed.data.message
          : t("social_login_error", "Login failed. Please try again.");
      setError(msg);
      onError?.(msg);
    },
    [onError, t],
  );

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.addEventListener("signin", handleSignIn);
    el.addEventListener("signinerror", handleSignInError);
    return () => {
      el.removeEventListener("signin", handleSignIn);
      el.removeEventListener("signinerror", handleSignInError);
    };
  }, [handleSignIn, handleSignInError]);

  return (
    <div>
      {error && (
        <div className="gcw:mb-2">
          <AlertBanner variant="danger">
            <span role="alert">{error}</span>
          </AlertBanner>
        </div>
      )}
      {loading && <p className="gcw:text-body gcw:text-text-secondary">{t("social_login_loading", "Signing in...")}</p>}
      <dmn-sign-in
        ref={ref}
        auth-api-host={authApiHost}
        client-id={clientId}
        client-secret={clientSecret}
        app-name="collins-widget"
        source="collins-widget"
        hide-sign-up="true"
        hide-email-sign-in="true"
      />
    </div>
  );
}
